import EntityExampleWrapper from 'client/components/core/EntityExampleWrapper'
import { usePage } from 'client/store'
import pageSelectors from 'client/store/page/pageSelectors'
import { slugs } from 'common/constants/inputTypes'
import { FieldSlugEnum } from 'common/enums/FieldSlugEnum'
import React, { useEffect } from 'react'
import { contactFieldsQuery } from 'src/editor/api/queries/contact-fields-query'
import { useCalculateFormFieldStyles } from 'src/editor/shared/hooks/use-calculate-form-fields-styles'
import { getContactFieldTitle } from 'src/editor/shared/utils/get-contact-field-title'
import { Field } from 'src/shared/components/field'
import { FieldIconNew } from 'src/shared/components/field-icon-new'
import { FieldWrapper } from 'src/shared/components/field-wrapper'
import { PhoneNumberFieldNew } from 'src/shared/components/phone-number-field-new'
import { SelectNew } from 'src/shared/components/select-new'
import { ContactUsFieldEntityInterface } from 'src/shared/entities/contact-us-field/contact-us-field-entity.schema'
import { FieldEntityInterface } from 'src/shared/entities/field/field-entity.schema'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { FieldSubEntityInterface } from 'src/shared/schemas/fields/field-sub-entity.schema'

interface BaseEditorFieldProps {
  entity:
    | FieldEntityInterface
    | ContactUsFieldEntityInterface
    | FieldSubEntityInterface
}

export const BaseEditorField = ({ entity }: BaseEditorFieldProps) => {
  const { t } = useTypedTranslation()
  const { data: contactFields } = contactFieldsQuery.useQuery()
  const [inputTypeName, setInputTypeName] = React.useState(
    t('entities.field.warnings.no_input_name'),
  )

  useEffect(() => {
    if (
      entity.slug === slugs.PASSWORD ||
      entity.slug === slugs.CONFIRM_PASSWORD
    ) {
      setInputTypeName('PASSWORD')
      return
    }

    if (entity.slug === FieldSlugEnum.TwoFactorAuthCode) {
      setInputTypeName('website_settings.login.slug.code')
      return
    }

    if (entity.slug && contactFields?.length) {
      const field = contactFields.find(({ slug }) => slug === entity.slug)
      if (field) {
        setInputTypeName(getContactFieldTitle(field, t))
      }
    } else {
      setInputTypeName(t('entities.field.warnings.no_input_name'))
    }
  }, [t, entity.slug, contactFields])

  const iconColor = entity.color || 'initial'
  const mobileIconColor = entity.mobileColor || iconColor

  const {
    calculatedBorder,
    calculatedPadding,
    calculatedFontSize,
    calculatedLineHeight,
    calculatedTextAlign,
    calculatedFontFamily,
    calculatedColor,
    calculatedBackgroundColor,
  } = useCalculateFormFieldStyles({
    border: entity.border,
    mobileBorder: entity.mobileBorder,
    padding: entity.padding,
    mobilePadding: entity.mobilePadding,
    fontSize: entity.fontSize,
    mobileFontSize: entity.mobileFontSize,
    lineHeight: entity.lineHeight,
    mobileLineHeight: entity.mobileLineHeight,
    textAlign: entity.textAlign,
    mobileTextAlign: entity.mobileTextAlign,
    color: entity.color,
    mobileColor: entity.mobileColor,
    backgroundColor: entity.backgroundColor,
    mobileBackgroundColor: entity.mobileBackgroundColor,
  })

  if (entity.slug === slugs.COUNTRY) {
    return (
      <SelectNew
        placeholderColor={entity.placeholderColor}
        mobilePlaceholderColor={entity.mobilePlaceholderColor}
        fontFamily={calculatedFontFamily.desktop}
        mobileFontFamily={calculatedFontFamily.mobile}
        textAlign={calculatedTextAlign.desktop}
        mobileTextAlign={calculatedTextAlign.mobile}
        lineHeight={calculatedLineHeight.desktop}
        mobileLineHeight={calculatedLineHeight.mobile}
        padding={calculatedPadding.desktop}
        mobilePadding={calculatedPadding.mobile}
        border={calculatedBorder.desktop}
        mobileBorder={calculatedBorder.mobile}
        backgroundColor={calculatedBackgroundColor.desktop}
        mobileBackgroundColor={calculatedBackgroundColor.mobile}
        color={calculatedColor.desktop}
        mobileColor={calculatedColor.mobile}
        fontSize={calculatedFontSize.desktop}
        mobileFontSize={calculatedFontSize.mobile}
        boxShadow={entity.boxShadow}
        mobileBoxShadow={entity.mobileBoxShadow}
      >
        <option>{t('entities.field.country.placeholder')}</option>
      </SelectNew>
    )
  }

  return (
    <EntityExampleWrapper
      label={inputTypeName}
      warning={Boolean(inputTypeName) === false}
    >
      <FieldWrapper>
        {entity.iconClassName && (
          <FieldIconNew
            color={iconColor}
            fontSize={calculatedFontSize.desktop}
            mobileFontSize={calculatedFontSize.mobile}
            mobileColor={mobileIconColor}
            className={entity.iconClassName}
          />
        )}
        {entity.slug === slugs.PHONE_NUMBER ? (
          <PhoneNumberFieldNew
            fontFamily={calculatedFontFamily.desktop}
            mobileFontFamily={calculatedFontFamily.mobile}
            hasIcon={!!entity.iconClassName}
            textAlign={calculatedTextAlign.desktop}
            mobileTextAlign={calculatedTextAlign.mobile}
            padding={calculatedPadding.desktop}
            mobilePadding={calculatedPadding.mobile}
            border={calculatedBorder.desktop}
            mobileBorder={calculatedBorder.mobile}
            backgroundColor={calculatedBackgroundColor.desktop}
            mobileBackgroundColor={calculatedBackgroundColor.mobile}
            color={calculatedColor.desktop}
            mobileColor={calculatedColor.mobile}
            placeholder={entity.placeholder}
            placeholderColor={entity.placeholderColor}
            mobilePlaceholderColor={entity.mobilePlaceholderColor}
            fontSize={calculatedFontSize.desktop}
            mobileFontSize={calculatedFontSize.mobile}
            lineHeight={calculatedLineHeight.desktop}
            mobileLineHeight={calculatedLineHeight.mobile}
            boxShadow={entity.boxShadow}
            mobileBoxShadow={entity.mobileBoxShadow}
            inputProps={{ readOnly: true }}
            countrySelectorProps={{
              buttonProps: { onClick: undefined },
              buttonStyle: { cursor: 'default' },
            }}
          />
        ) : (
          <Field
            fontFamily={calculatedFontFamily.desktop}
            mobileFontFamily={calculatedFontFamily.mobile}
            placeholder={entity.placeholder}
            padding={calculatedPadding.desktop}
            lineHeight={calculatedLineHeight.desktop}
            mobileLineHeight={calculatedLineHeight.mobile}
            textAlign={calculatedTextAlign.desktop}
            mobileTextAlign={calculatedTextAlign.mobile}
            mobilePadding={calculatedPadding.mobile}
            id={entity.htmlAttrId}
            border={calculatedBorder.desktop}
            mobileBorder={calculatedBorder.mobile}
            backgroundColor={calculatedBackgroundColor.desktop}
            mobileBackgroundColor={calculatedBackgroundColor.mobile}
            color={calculatedColor.desktop}
            mobileColor={calculatedColor.mobile}
            placeholderColor={entity.placeholderColor}
            mobilePlaceholderColor={entity.mobilePlaceholderColor}
            fontSize={calculatedFontSize.desktop}
            mobileFontSize={calculatedFontSize.mobile}
            boxShadow={entity.boxShadow}
            mobileBoxShadow={entity.mobileBoxShadow}
          />
        )}
      </FieldWrapper>
    </EntityExampleWrapper>
  )
}
